import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CoachMarkdown } from "@/components/CoachMarkdown";

export default function Coach() {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const { data: history } = useQuery({ queryKey: ["coach-history"], queryFn: async () => (await api.get("/coach/history")).data });
  useEffect(() => { if (history?.length) setMessages(history.map((m) => ({ role: m.role, content: m.content }))); }, [history]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, streaming]);

  const suggestions = lang === "id"
    ? ["Bagaimana progres saya minggu ini?", "Beri saya tips menurunkan lemak", "Program latihan untuk pemula?"]
    : ["How is my progress this week?", "Give me fat loss tips", "Beginner workout program?"];

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) {
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Maaf, AI coach sedang tidak tersedia. Coba lagi." }; return c; });
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Error. Coba lagi." }; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <div className="mx-auto flex h-[calc(100dvh_-_12.5rem_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom))] lg:h-[calc(100dvh-11rem)] max-w-2xl flex-col">
      <SectionHeader title="AI Coach" subtitle="FitBersama Coach" />
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto no-scrollbar pb-4">
        {messages.length === 0 && (
          <div className="rounded-2xl border bg-card p-6 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-primary/15 text-primary"><Sparkles className="h-6 w-6" /></div>
            <p className="mt-3 text-sm text-muted-foreground">{t("coachIntro")}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid={`coach-suggest-${i}`}
                  className="rounded-full border px-3 py-1.5 text-xs transition-colors hover:bg-accent">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={cn("max-w-[85%] rounded-2xl px-4 py-2.5 text-sm",
              m.role === "user" ? "whitespace-pre-wrap bg-primary text-primary-foreground" : "border bg-card")}
              data-testid={`coach-msg-${m.role}`}>
              {!m.content ? <Loader2 className="h-4 w-4 animate-spin" /> : m.role === "user" ? m.content : <CoachMarkdown>{m.content}</CoachMarkdown>}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t pt-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("askCoach")} disabled={streaming} data-testid="coach-input" />
        <Button type="submit" disabled={streaming || !input.trim()} size="icon" data-testid="coach-send">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
